import styled from 'styled-components';
import { Message, Segment, Table } from 'semantic-ui-react';

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ImportShell = styled.div`
  border: 1px solid #dfe7ef;
  border-radius: 10px;
  background: #ffffff;
  overflow: hidden;
`;

export const StepsBar = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto 1fr auto 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 24px 32px;
  background: #eef3f8;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const StepLabel = styled.div<{ active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${props => (props.active ? '#1f2937' : '#6b7280')};
  font-size: 0.95rem;
  white-space: nowrap;
`;

export const StepCircle = styled.span<{ active: boolean; completed: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 2px solid
    ${props => (props.active || props.completed ? '#32a06d' : '#ffffff')};
  background: ${props => (props.completed ? '#32a06d' : '#ffffff')};
  color: ${props => (props.completed ? '#ffffff' : '#1f2937')};
  font-weight: 600;
`;

export const StepConnector = styled.div<{ completed: boolean }>`
  height: 2px;
  min-width: 80px;
  background: ${props => (props.completed ? '#32a06d' : '#ffffff')};

  @media (max-width: 900px) {
    display: none;
  }
`;

export const ContentSegment = styled(Segment)`
  &&& {
    border: none;
    border-radius: 0;
    box-shadow: none;
    margin: 0;
    min-height: 460px;
    padding: 32px;
  }
`;

export const PageHeader = styled.header`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 24px;
`;

export const PageTitle = styled.h1`
  margin: 0;
  font-size: 1.65rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.3;
`;

export const PageSubtitle = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: #6b7280;
  line-height: 1.5;
  max-width: 760px;
`;

export const MutedText = styled.p`
  margin: -8px 0 18px;
  color: #6b7280;
`;

export const ExpectedFieldsTable = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(180px, 1fr);
  overflow-x: auto;
  border: 1px solid #dfe7ef;
  border-radius: 8px;
  margin-bottom: 16px;

  > div {
    min-width: 0;
    padding: 12px;
    border-right: 1px solid #eef2f7;
  }

  strong,
  span {
    display: block;
    white-space: normal;
    overflow-wrap: anywhere;
    line-height: 1.35;
  }

  strong {
    color: #1f2937;
    font-size: 0.78rem;
    text-transform: uppercase;
    min-height: 42px;
  }

  span {
    color: #7b8794;
    margin-top: 10px;
  }
`;

export const Dropzone = styled.div<{ isDragActive: boolean }>`
  display: flex;
  min-height: 360px;
  align-items: center;
  justify-content: center;
  padding: 24px;
  border: 2px dashed
    ${props => (props.isDragActive ? props.theme.main.color : '#1683d8')};
  border-radius: 6px;
  background: ${props => (props.isDragActive ? '#f3f8fd' : '#ffffff')};
  cursor: pointer;
  text-align: center;
  color: #1f2937;
`;

export const ActionsBar = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 32px;
  background: #eef3f8;
  border-top: 1px solid #dfe7ef;
`;

export const ActionsGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &&& .button {
    min-width: 220px;
  }
`;

export const ScrollArea = styled.div<{ allowDropdownOverflow?: boolean }>`
  overflow-x: auto;
  overflow-y: ${props => (props.allowDropdownOverflow ? 'visible' : 'hidden')};
  min-height: ${props => (props.allowDropdownOverflow ? '124px' : 'auto')};
  padding-bottom: ${props => (props.allowDropdownOverflow ? '18px' : '12px')};
  border: 1px solid #dfe7ef;
  border-radius: 10px;
  margin-bottom: 28px;
  position: relative;
  z-index: ${props => (props.allowDropdownOverflow ? 20 : 1)};

  .import-match-dropdown {
    position: relative;
    z-index: 30;
  }

  .import-match-dropdown .menu {
    max-height: 220px;
    overflow-y: auto;
    z-index: 9999 !important;
  }
`;

export const MatchGrid = styled.div<{ columnCount: number }>`
  display: grid;
  grid-template-columns: repeat(${props => Math.max(props.columnCount, 1)}, 240px);
  min-width: max-content;
`;

export const MatchColumn = styled.div`
  min-height: 72px;
  padding: 12px 18px;
  border-right: 1px solid #eef2f7;
`;

export const MatchSelect = styled.select`
  width: 100%;
  min-height: 38px;
  padding: 0 36px 0 14px;
  border: 1px solid #d8dce3;
  border-radius: 4px;
  background: #ffffff;
  color: #1f2937;
  font-size: 0.92rem;
  outline: none;
  position: relative;
  z-index: 999999;

  &:focus {
    border-color: ${props => props.theme.main.color};
    box-shadow: 0 0 0 1px ${props => props.theme.main.color};
  }
`;

export const HeaderPreview = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: #1f2937;
  margin-bottom: 18px;

  button {
    border: none;
    border-radius: 6px;
    background: #eef3f8;
    color: #374151;
    cursor: pointer;
    width: 22px;
    height: 22px;
  }
`;

export const MatchPreview = styled.div`
  color: #9ca3af;
  margin-bottom: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ValidationTools = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  margin-bottom: 16px;
`;

export const TableWrapper = styled.div`
  overflow: auto;
  max-height: 560px;
  border: 1px solid #dfe7ef;
  border-radius: 8px;

  .ui.table {
    margin: 0;
    border: none;
    border-radius: 8px;
  }

  .ui.table thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #ffffff !important;
    color: #1f2937;
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    border-bottom: 1px solid #dfe7ef !important;
  }
`;

export const StyledTable = styled(Table)`
  &&& {
    font-size: 0.9rem;
    min-width: 1100px;
  }
`;

export const DataCell = styled.td<{ hasError: boolean }>`
  &&& {
    background: ${props => (props.hasError ? '#fff1f2' : '#ffffff')};
    color: #1f2937;
  }
`;

export const StatusMessage = styled(Message)`
  &&& {
    border-radius: 8px;
  }
`;
